import React, { useMemo } from 'react'
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js'
import { Doughnut } from 'react-chartjs-2'
import { ICycleSession } from 'pages/Planning/types/cycleSession'

// Register Chart.js components
ChartJS.register(ArcElement, Tooltip, Legend)
interface IStudyDistributionChartProps {
  sessions: Array<ICycleSession>
  totalHours: number
  isDarkMode?: boolean
  addProgressRing?: boolean
}

const StudyDistributionChart: React.FC<IStudyDistributionChartProps> = ({
  sessions,
  totalHours,
  isDarkMode = false,
  addProgressRing = false,
}) => {
  const chartData = useMemo(() => {
    const labels = sessions.map(
      (s, idx) => `${String(idx + 1).padStart(2, '0')}. ${s.module.subject}`
    )

    const data = sessions.map((s) => s.hours)
    const backgroundColor = sessions.map((s) => s.color)

    const statusRingData = sessions.map((s) => s.hours)

    const statusRingBackground = sessions.map((s) =>
      s.status === 'done' ? s.color + '88' : isDarkMode ? '#333' : '#eee'
    )

    const datasets = [
      {
        label: 'Sessão de Estudo',
        data,
        backgroundColor,
        borderColor: isDarkMode ? '#1a1a1a' : '#ffffff',
        borderWidth: 1,
        hoverOffset: 6,
      },
      ...(addProgressRing
        ? [
            {
              label: 'Status',
              data: statusRingData,
              backgroundColor: statusRingBackground,
              borderColor: isDarkMode ? '#1a1a1a' : '#ffffff',
              borderWidth: 1,
              cutout: '75%',
              circumference: 360,
            },
          ]
        : []),
    ].filter(Boolean) as any[]

    return {
      labels,
      datasets,
    }
  }, [sessions, isDarkMode, addProgressRing])

  // Chart options
  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: true,
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          backgroundColor: isDarkMode ? '#2a2a2a' : '#ffffff',
          titleColor: isDarkMode ? '#ffffff' : '#333333',
          bodyColor: isDarkMode ? '#e0e0e0' : '#666666',
          borderColor: isDarkMode ? '#444444' : '#e0e0e0',
          borderWidth: 1,
          padding: 12,
          displayColors: true,
          callbacks: {
            label: function (context: any) {
              const label = context.label || ''
              const value = context.parsed || 0
              const hours = Math.floor(value)
              const minutes = Math.round((value % 1) * 60)
              const timeStr =
                hours > 0 ? `${hours}h${minutes > 0 ? ` ${minutes}min` : ''}` : `${minutes}min`
              return `${label} - ${timeStr}`
            },
          },
        },
      },
      cutout: '70%',
    }),
    [isDarkMode, totalHours, sessions]
  )

  const checkPlugin = {
    id: 'checkPlugin',
    afterDraw(chart) {
      const { ctx, chartArea } = chart
      // meta do dataset 0 (o anel principal)
      const meta = chart.getDatasetMeta(0)
      if (!meta || !meta.data) return

      meta.data.forEach((arc, index) => {
        const session = sessions[index]
        if (!session || (session.status !== 'done' && session.status !== 'in_progress')) return

        // propriedades do arco
        const startAngle = arc.startAngle
        const endAngle = arc.endAngle
        const innerRadius = arc.innerRadius
        const outerRadius = arc.outerRadius
        const centerX = arc.x
        const centerY = arc.y

        // ângulo médio e raio médio (ponto central da fatia)
        const angle = (startAngle + endAngle) / 2
        const radius = (innerRadius + outerRadius) / 2

        // opcional: aproximar o check mais para dentro/fora se necessário
        const radiusMultiplier = 0.855 // 1.0 = exatamente no meio; <1 move para dentro, >1 move para fora
        const x = centerX + Math.cos(angle) * radius * radiusMultiplier
        const y = centerY + Math.sin(angle) * radius * radiusMultiplier

        // desenha o check
        ctx.save()

        // ajuste de contraste: branco no dark, preto no light
        ctx.fillStyle = isDarkMode ? '#ffffff' : '#000000'
        // tamanho da fonte proporcional ao tamanho do gráfico
        const baseFontSize = Math.max(12, Math.min(20, Math.round((outerRadius - innerRadius) / 3)))
        ctx.font = `bold ${baseFontSize}px sans-serif`
        ctx.textAlign = 'center'
        ctx.color = isDarkMode ? '#ffffff' : '#000000'
        ctx.textBaseline = 'middle'
        ctx.fillText(session.status === 'done' ? '✓' : '●', x, y)
        ctx.restore()
      })
    },
  }

  return (
    <div className={`study-distribution-chart-container ${isDarkMode ? 'dark' : 'light'}`}>
      <Doughnut data={chartData} options={options} plugins={[checkPlugin]} />
      <div className="chart-label">
        <div className="chart-label-total-value">{totalHours.toFixed(1)}h</div>
        <div className="chart-label-total">Total Semanal</div>
      </div>
    </div>
  )
}

export default StudyDistributionChart
